import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MapPin, X, CircleNotch, Check } from '@phosphor-icons/react';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

export const LocationBanner = () => {
  const [status, setStatus] = useState('checking');
  const [location, setLocation] = useState('');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    checkLocation();
    return () => { mountedRef.current = false; };
  }, []);

  const checkLocation = async () => {
    if (localStorage.getItem('location_dismissed') === 'true') {
      setStatus('dismissed');
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/assistant/location`, { withCredentials: true });
      if (!mountedRef.current) return;
      if (res.data.location) {
        setLocation(res.data.location);
        setStatus('done');
      } else {
        setStatus('ask');
      }
    } catch {
      if (mountedRef.current) setStatus('ask');
    }
  };

  const shareLocation = async () => {
    setStatus('detecting');

    // Try browser geolocation first
    if (navigator.geolocation) {
      try {
        const position = await new Promise((resolve, reject) => {
          const timer = setTimeout(() => reject(new Error('timeout')), 7000);
          navigator.geolocation.getCurrentPosition(
            (pos) => { clearTimeout(timer); resolve(pos); },
            (err) => { clearTimeout(timer); reject(err); },
            { timeout: 6000, enableHighAccuracy: false }
          );
        });

        if (!mountedRef.current) return;
        const res = await axios.post(`${API_URL}/api/assistant/location`, {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }, { withCredentials: true });

        if (!mountedRef.current) return;
        if (res.data.location) {
          setLocation(res.data.location);
          setStatus('done');
          return;
        }
      } catch {
        // Geolocation failed — fall through to auto-detect
      }
    }

    // Fallback: auto-detect from user's task data
    if (!mountedRef.current) return;
    try {
      const res = await axios.post(`${API_URL}/api/assistant/location`, {
        auto_detect: true,
      }, { withCredentials: true });

      if (!mountedRef.current) return;
      if (res.data.location) {
        setLocation(res.data.location);
        setStatus('done');
      } else {
        dismiss();
      }
    } catch {
      if (mountedRef.current) dismiss();
    }
  };

  const dismiss = () => {
    localStorage.setItem('location_dismissed', 'true');
    setStatus('dismissed');
  };

  if (status === 'checking' || status === 'dismissed') return null;

  if (status === 'done') {
    // Show confirmation briefly, then hide after 3 seconds
    setTimeout(() => { if (mountedRef.current) setStatus('dismissed'); }, 3000);
    return (
      <div className="mx-4 mt-2" data-testid="location-detected">
        <div className="flex items-center gap-2 bg-[#f0fdf4] border border-[#bbf7d0] rounded-xl px-4 py-2 animate-pulse">
          <Check size={14} className="text-[#16a34a]" weight="bold" />
          <span className="text-xs text-[#16a34a] font-medium">Location set: {location}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-4 mt-2" data-testid="location-banner">
      {status === 'ask' && (
        <div className="flex items-center justify-between bg-[#f0f7ff] border border-[#bfdbfe] rounded-xl px-4 py-2.5">
          <div className="flex items-center gap-2.5">
            <MapPin size={16} className="text-[#2563eb] flex-shrink-0" weight="fill" />
            <span className="text-xs text-[#1e40af]">Share location for more relevant local results</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={shareLocation}
              className="text-xs font-semibold text-[#2563eb] hover:underline"
              data-testid="share-location-btn"
            >
              Enable
            </button>
            <button onClick={dismiss} className="p-1 rounded hover:bg-[#dbeafe] transition-colors">
              <X size={12} className="text-[#93c5fd]" />
            </button>
          </div>
        </div>
      )}

      {status === 'detecting' && (
        <div className="flex items-center gap-2.5 bg-[#f0f7ff] border border-[#bfdbfe] rounded-xl px-4 py-2.5">
          <CircleNotch size={14} className="text-[#2563eb] animate-spin" weight="bold" />
          <span className="text-xs text-[#1e40af]">Detecting your location...</span>
        </div>
      )}
    </div>
  );
};
